import { defineStore } from 'pinia';

export const useCoursesStore = defineStore('courses', {
    state: () => ({
        course: null,
        section: null,
        chapter: null,
        currentChapter: null,
        loadingCourse: false,
        error: null,
        courseSidebarOpen: false,
        previousProgress: 0,
    }),
    getters: {
        chapters: (state) => {
            let chapters = [];
            state.course?.sections?.forEach((section) => {
                section.chapters.forEach((chapter) => {
                    chapters.push(chapter);
                });
            });

            return chapters;
        },
        progress: (state) => {
            const totalComplete = state.course?.sections?.reduce(
                (count, section) => {
                    const chapterSum = section.chapters.reduce(
                        (chapterAcc, chapter) => {
                            return (
                                chapterAcc +
                                (chapter.user_status === 'complete' ? 1 : 0)
                            );
                        },
                        0
                    );
                    return count + chapterSum;
                },
                0
            );

            return state.chapters.length === 0 ? 0 : Math.round((totalComplete / state.chapters.length) * 100);
        },
        startNextChapter: (state) => {
            const newChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'new';
            });

            if (newChapter) {
                return newChapter;
            }

            return null;
        },
        finishChapter: (state) => {
            const leftOverChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'started';
            });

            if (leftOverChapter) {
                return leftOverChapter;
            }

            return null;
        },
        nextChapter: (state) => {
            const chapterIndex = state.chapters.indexOf(state.currentChapter);

            const leftOverChapter = state.chapters.find((chapter) => {
                return chapter.user_status === 'started';
            });

            if (state.chapters[chapterIndex + 1]) {
                return state.chapters[chapterIndex + 1];
            }

            if (leftOverChapter) {
                return leftOverChapter;
            }

            return null;
        },
        previousChapter: (state) => {
            const chapterIndex = state.chapters.indexOf(state.currentChapter);
            return state.chapters[chapterIndex - 1];
        },
    },
    actions: {
        async getFirstCourse() {
            this.setLoadingCourse(true);
            return ApiService.apiCall('/courses/first-course/', 'get')
                .then((response) => {
                    this.setLoadingCourse(false);
                    if (response && response.data && response.data.course) {
                        this.course = response.data.course;
                        return response.data;
                    } else if (
                        response &&
                        response.data &&
                        response.data.error
                    ) {
                        // Handle API error response
                        throw new Error(
                            response.data.message || response.data.error
                        );
                    } else {
                        throw new Error(
                            'Invalid response format: missing course data'
                        );
                    }
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    // Don't throw for expected auth failures during page load (SUPPORT-208)
                    // The bootstrap.js interceptor already handles 401 redirect to login
                    if (e.response?.status === 401) {
                        console.warn(
                            'Course loading failed - user not authenticated'
                        );
                        return null;
                    }
                    // Handle HTTP error responses (404, 500, etc.)
                    if (
                        e.response &&
                        e.response.data &&
                        e.response.data.error
                    ) {
                        const errorMessage =
                            e.response.data.message || e.response.data.error;
                        throw new Error(
                            'Error getting course. ' + errorMessage
                        );
                    }
                    const errorMessage =
                        e?.message || e?.toString() || 'Unknown error occurred';
                    throw new Error('Error getting course. ' + errorMessage);
                });
        },
        async startChapter(chapterUuid) {
            return ApiService.apiCall(
                `/course/chapters/${chapterUuid}/start`,
                'get'
            )
                .then((response) => {
                    this.setChapterStatus(chapterUuid, 'started');
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error('Error starting chapter. ' + e.message);
                });
        },
        async completeChapter(chapterUuid) {
            return ApiService.apiCall(
                `/course/chapters/${chapterUuid}/complete`,
                'get'
            )
                .then((response) => {
                    this.setChapterStatus(chapterUuid, 'complete');
                    return response.data;
                })
                .catch((e) => {
                    this.setLoadingCourse(false);
                    throw new Error(
                        'Error marking chapter complete. ' + e.message
                    );
                });
        },
        setError(val) {
            this.error = val;
        },
        setCourse(course) {
            this.course = course;
        },
        setCurrentChapter(chapter) {
            this.currentChapter = chapter;
        },
        setChapterStatus(uuid, status) {
            if (!this.course || !this.course.sections) {
                return;
            }

            this.course.sections.forEach((section) => {
                section.chapters.forEach((chapter) => {
                    if (chapter.uuid === uuid) {
                        chapter.user_status = status;
                    }
                });
            });
        },
        setLoadingCourse(val) {
            this.loadingCourse = val;
        },
        setCourseSidebarOpen() {
            this.courseSidebarOpen = !this.courseSidebarOpen;
        },
    },
});
